/** @file sheetIslandsOf: the islands of an exploded sheet, merge gap and noise floor sized to it. */
import { islandsOf } from '@zilo/pose3d-image-io/island/islandsOf';
import type { IslandMap } from '@zilo/pose3d-image-io/island/islandTypes';
import type { Image } from '@zilo/pose3d-image-io/types/image';

/** An island under this share of the median island's pixels is noise (a phone's buttons, a stray speck). */
const NOISE = 0.2;

/** The merge gap for a sheet: about 1/360 of its long side, at least 2 px (8 px on a phone screenshot). */
function sheetMergeOf(image: Pick<Image, 'width' | 'height'>): number {
  return Math.max(2, Math.round(Math.max(image.width, image.height) / 360));
}

/**
 * `islandsOf` with `merge` (default `sheetMergeOf`) and then every island under a fifth of the median island's size
 * dropped from `islands` (labels stay; a dropped island is simply not offered).
 */
export function sheetIslandsOf(image: Image, merge = sheetMergeOf(image)): IslandMap {
  const map = islandsOf(image, { merge, minPixels: 4 });
  const sizes = map.islands.map((i) => i.pixels).sort((a, b) => a - b);
  const median = sizes[Math.floor(sizes.length / 2)] ?? 0;
  return { ...map, islands: map.islands.filter((i) => i.pixels >= NOISE * median) };
}
